import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type ConsoleMarkdownProps = {
  content: string;
  tone?: 'assistant' | 'user' | 'system' | 'tool';
};

const toneClasses: Record<NonNullable<ConsoleMarkdownProps['tone']>, string> = {
  assistant: 'text-slate-100',
  user: 'text-slate-100',
  system: 'text-slate-300',
  tool: 'text-slate-200',
};

const codeToneClasses: Record<NonNullable<ConsoleMarkdownProps['tone']>, { frame: string; text: string; inline: string }> = {
  assistant: {
    frame: 'text-emerald-300',
    text: 'text-emerald-100',
    inline: 'text-emerald-200 bg-emerald-400/10',
  },
  user: {
    frame: 'text-blue-300',
    text: 'text-blue-100',
    inline: 'text-blue-200 bg-blue-400/10',
  },
  system: {
    frame: 'text-slate-400',
    text: 'text-slate-200',
    inline: 'text-slate-100 bg-white/10',
  },
  tool: {
    frame: 'text-amber-300',
    text: 'text-amber-100',
    inline: 'text-amber-200 bg-amber-400/10',
  },
};

const getCodeString = (children: React.ReactNode): string => {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) {
    return children.map(getCodeString).join('');
  }
  if (React.isValidElement(children)) {
    return getCodeString(children.props.children);
  }
  return '';
};

const getLanguageLabel = (className?: string) => {
  const match = className?.match(/language-([a-zA-Z0-9_+-]+)/);
  return match?.[1] || 'text';
};

const ConsoleCodeBlock: React.FC<{
  code: string;
  language: string;
  tone: NonNullable<ConsoleMarkdownProps['tone']>;
}> = ({ code, language, tone }) => {
  const colors = codeToneClasses[tone];
  const lines = code.replace(/\n$/, '').split('\n');
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="my-2 overflow-x-auto font-mono text-sm leading-7 select-text">
      <div className={`flex items-center justify-between gap-3 ${colors.frame}`}>
        <span>{`+--[${language}]`}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="select-none text-xs underline underline-offset-2 opacity-80 hover:opacity-100"
          aria-label={`Copy ${language} code block`}
        >
          [{copied ? 'copied' : 'copy'}]
        </button>
      </div>
      {lines.map((line, index) => (
        <div key={`${language}-${index}`} className={`whitespace-pre ${colors.text}`}>
          {`| ${line || ' '}`}
        </div>
      ))}
      <div className={colors.frame}>+--</div>
    </div>
  );
};

const ConsoleMarkdown: React.FC<ConsoleMarkdownProps> = ({ content, tone = 'assistant' }) => {
  const colors = codeToneClasses[tone];

  return (
    <div className={`font-mono text-sm leading-7 select-text ${toneClasses[tone]}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <div className="mb-2 whitespace-pre-wrap break-words last:mb-0">{children}</div>,
          strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
          em: ({ children }) => <em className="text-slate-300 italic">{children}</em>,
          h1: ({ children }) => <div className="mb-2 text-cyan-300">{`# `}{children}</div>,
          h2: ({ children }) => <div className="mb-2 text-cyan-300">{`## `}{children}</div>,
          h3: ({ children }) => <div className="mb-2 text-cyan-300">{`### `}{children}</div>,
          ul: ({ children }) => <ul className="mb-2 list-inside list-disc space-y-1 pl-1">{children}</ul>,
          ol: ({ children }) => <ol className="mb-2 list-inside list-decimal space-y-1 pl-1">{children}</ol>,
          li: ({ children }) => <li className="whitespace-pre-wrap break-words">{children}</li>,
          blockquote: ({ children }) => (
            <div className="my-2 border-l border-slate-600 pl-3 text-slate-400">
              <span className="mr-2 text-slate-500">&gt;</span>
              {children}
            </div>
          ),
          hr: () => <div className="my-2 text-slate-500">----------------------------------------</div>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="break-all text-cyan-300 underline underline-offset-2"
            >
              {children}
            </a>
          ),
          code: (props) => {
            const { className, children } = props;
            const inline = Boolean((props as { inline?: boolean }).inline);
            const code = getCodeString(children);

            if (inline) {
              return (
                <code className={`rounded px-1 py-0.5 ${colors.inline}`}>
                  {code}
                </code>
              );
            }

            return (
              <ConsoleCodeBlock
                code={code}
                language={getLanguageLabel(className)}
                tone={tone}
              />
            );
          },
          pre: ({ children }) => <>{children}</>,
          table: ({ children }) => <div className="my-2 overflow-x-auto">{children}</div>,
          thead: ({ children }) => <div className="mb-1 text-cyan-300">{children}</div>,
          tbody: ({ children }) => <div className="space-y-1">{children}</div>,
          tr: ({ children }) => <div className="whitespace-pre-wrap break-words">{children}</div>,
          th: ({ children }) => <span className="mr-4 font-semibold text-cyan-300">{children}</span>,
          td: ({ children }) => <span className="mr-4 text-slate-200">{children}</span>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default ConsoleMarkdown;